"use client";
import React, { CSSProperties, useEffect, useState } from "react";

import { useCurrencyStore } from "@/store/currency.store";
import { ConvertedPrices } from "@/types/pricing.types";
import { cn } from "@/utils/cn";

import { Amount } from "./Amount";
import { AmountVariant } from "./types";

export interface RangeAmountProps {
  convertedPricesMin?: ConvertedPrices;
  convertedPricesMax?: ConvertedPrices;
  variant?: AmountVariant;
  style?: CSSProperties;
  className?: string;
}

// Helper hook to detect client-side mount
const useHasMounted = (): boolean => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
};

/**
 * Render price range using two `Amount` components to ensure
 * identical behaviour (currency switch, formatting) with the rest of UI.
 */
export const RangeAmount: React.FC<RangeAmountProps> = ({
  convertedPricesMin,
  convertedPricesMax,
  variant = AmountVariant.FULL,
  style,
  className,
}) => {
  const hasMounted = useHasMounted();
  const { currency } = useCurrencyStore();

  // Avoid hydration mismatch: render placeholder until mounted
  if (!hasMounted) {
    return (
      <span
        className={cn(className)}
        style={style}
      ></span>
    );
  }

  // If both sides present – check existence and equality for current currency
  if (convertedPricesMin && convertedPricesMax) {
    const minVal = convertedPricesMin[currency.value as keyof ConvertedPrices];
    const maxVal = convertedPricesMax[currency.value as keyof ConvertedPrices];

    // If one of the sides doesn't have value for current currency – fallback to single Amount
    if (minVal === undefined || maxVal === undefined) {
      return (
        <Amount
          convertedPrices={convertedPricesMin || convertedPricesMax}
          variant={variant}
          style={style}
          className={className}
        />
      );
    }

    // Same values – render single Amount
    if (Number(minVal) === Number(maxVal)) {
      return (
        <Amount
          convertedPrices={convertedPricesMin}
          variant={variant}
          style={style}
          className={className}
        />
      );
    }

    // Valid different values – render range
    return (
      <span
        className={cn("flex gap-1 items-center flex-wrap", className)}
        style={style}
      >
        <Amount
          convertedPrices={convertedPricesMin}
          variant={variant}
        />
        <span>&ndash;</span>
        <Amount
          convertedPrices={convertedPricesMax}
          variant={variant}
        />
      </span>
    );
  }

  // Fallback to single Amount when only one side provided
  return (
    <Amount
      convertedPrices={convertedPricesMin || convertedPricesMax}
      variant={variant}
      style={style}
      className={className}
    />
  );
};
